import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { pick } from 'lodash';

import { DB_DATE_FORMAT, SERVER_TIMEZONE } from 'core/constants';
import { PricingSourceType } from 'pricing/enums';
import { CostValidityEntry, VendorQuoteValidUntilPrefill } from 'quotes/enums';
import {
  ICostSourceFormValue,
  ICustomerQuotePart,
  IQuotePartCostData,
  ISelectedSpaFormValue,
  TVendorQuoteCostFormValue,
} from 'quotes/types';

dayjs.extend(utc);
dayjs.extend(timezone);

type TCostValue = number | string | null | undefined;

export interface IOneTimeSpaFacts {
  costSourceQuoteId?: number | null;
  costSourceType?: PricingSourceType | null;
  externalReplacementCost?: TCostValue;
  isShipAndDebitVendor?: boolean;
  purchaseCost?: TCostValue;
  replacementCost?: TCostValue;
}

export interface IOneTimeSpaState {
  // The dates the user gives; with NONE they are read-only.
  entry: CostValidityEntry;
  // A system cost not saved yet: its dates are known once the BE has applied it.
  isDatesPendingSave: boolean;
  isExpirationDateRequired: boolean;
  isOneTimeSpa: boolean;
  // False hides the dates.
  isShown: boolean;
  resolvedEffectiveDate: string | null;
  resolvedExpirationDate: string | null;
}

export type TOneTimeSpaRow = Pick<
  ICustomerQuotePart,
  | 'costSourceQuoteExternalNumber'
  | 'costSourceQuoteId'
  | 'costSourceQuoteStartDate'
  | 'costSourceQuoteValidUntil'
  | 'costSourceType'
  | 'costSourceValidSince'
  | 'costSourceValidUntil'
  | 'externalReplacementCost'
  | 'purchaseCost'
  | 'replacementCost'
  | 'supplier'
>;

// A vendor quote's identity and dates, from a saved row or the live cost.
type TVendorQuoteRow = Partial<
  Pick<
    TOneTimeSpaRow,
    | 'costSourceQuoteExternalNumber'
    | 'costSourceQuoteId'
    | 'costSourceQuoteStartDate'
    | 'costSourceQuoteValidUntil'
    | 'costSourceType'
    | 'costSourceValidUntil'
  >
>;

export interface IOneTimeSpaRowDates {
  isOneTimeSpa: boolean;
  // "YYYY-MM-DD"
  validSince: string | null;
  validUntil: string | null;
}

export const NOT_ONE_TIME_SPA_STATE: IOneTimeSpaState = {
  entry: CostValidityEntry.NONE,
  isDatesPendingSave: false,
  isExpirationDateRequired: false,
  isOneTimeSpa: false,
  isShown: false,
  resolvedEffectiveDate: null,
  resolvedExpirationDate: null,
};

const NON_VENDOR_QUOTE_COST_SOURCE_TYPES: PricingSourceType[] = [
  PricingSourceType.CORPORATE,
  PricingSourceType.CUSTOM,
  PricingSourceType.EXTERNAL,
  PricingSourceType.SELECTED_SPA,
  PricingSourceType.UNSET,
];

const toDay = (value?: string | null): string | null => value?.substring(0, 10) || null;

const toPositiveCost = (value: TCostValue): number => {
  const cost = Number(value);

  return Number.isFinite(cost) && cost > 0 ? cost : 0;
};

/**
 * The BE's one-time SPA rule (LineCostTypeService), for a cost it has not seen yet: the checks run
 * in its order and the first match wins.
 */
export const isOneTimeSpaLine = ({
  costSourceType,
  externalReplacementCost,
  isShipAndDebitVendor,
  purchaseCost,
  replacementCost,
}: IOneTimeSpaFacts): boolean => {
  const purchase = toPositiveCost(purchaseCost);
  if (!purchase) {
    return false;
  }

  if (PricingSourceType.SELECTED_SPA === costSourceType) {
    return false;
  }

  const manualReplacement = toPositiveCost(replacementCost);
  const externalReplacement = toPositiveCost(externalReplacementCost);
  if (!manualReplacement && !externalReplacement) {
    return false;
  }

  if (PricingSourceType.EXTERNAL === costSourceType) {
    return false;
  }

  if (purchase >= (manualReplacement || externalReplacement)) {
    return false;
  }

  return !!isShipAndDebitVendor;
};

/**
 * The BE's test (OneTimeSpaDatesService) for a cost whose dates come from its vendor quote: a
 * source that can point at a vendor quote, and the quote line behind it.
 */
export const isVendorQuoteCost = ({
  costSourceQuoteId,
  costSourceType,
}: Pick<IOneTimeSpaFacts, 'costSourceQuoteId' | 'costSourceType'>): boolean =>
  !!costSourceType &&
  !NON_VENDOR_QUOTE_COST_SOURCE_TYPES.includes(costSourceType) &&
  !!Number(costSourceQuoteId);

/**
 * The rule on form values only, so the fields, the schema and the payload agree: the replacement
 * cost field holds the external one unless overwritten, and an overwritten one left empty is
 * refused by the form first. Only for a cost the user gives dates to, never a pick or the engine's.
 */
export const isOneTimeSpaFormCost = ({
  isShipAndDebitVendor,
  purchaseCost,
  replacementCost,
}: Pick<IOneTimeSpaFacts, 'isShipAndDebitVendor' | 'purchaseCost' | 'replacementCost'>): boolean =>
  isOneTimeSpaLine({ isShipAndDebitVendor, purchaseCost, replacementCost });

// A vendor quote cost whose vendor quote has no Valid until: the user gives one on the line.
export const isVendorQuoteWithoutValidUntil = (row?: TVendorQuoteRow | null): boolean =>
  !!row && isVendorQuoteCost(row) && !row.costSourceQuoteValidUntil;

// QuotePartHelper.getVendorQuoteNumber names it by either, whichever the response carries.
const isSameVendorQuote = (
  { costSourceQuoteExternalNumber, costSourceQuoteId }: TVendorQuoteRow,
  vendorQuoteNumber: string
): boolean =>
  [costSourceQuoteExternalNumber, String(costSourceQuoteId)].includes(String(vendorQuoteNumber));

const getVendorQuoteId = (
  costData?: Pick<IQuotePartCostData, 'costSourceQuoteId' | 'costSourceType'> | null
): number | null =>
  costData && isVendorQuoteCost(costData) ? Number(costData.costSourceQuoteId) : null;

// Only an answer that names the vendor quote's dates (the pricing preview, the saved row) counts.
const getDatedVendorQuoteId = (costData?: IQuotePartCostData | null): number | null =>
  costData && undefined !== costData.costSourceQuoteValidUntil ? getVendorQuoteId(costData) : null;

/**
 * The live cost's vendor quote, so its dates show as soon as the cost comes in, before the line is
 * saved. Null for any other cost, and while its dates are not known.
 */
export const getVendorQuoteCost = (
  costData?: IQuotePartCostData | null
): TVendorQuoteCostFormValue | null =>
  getDatedVendorQuoteId(costData)
    ? pick(costData, [
        'costSourceQuoteExternalNumber',
        'costSourceQuoteId',
        'costSourceQuoteStartDate',
        'costSourceQuoteValidUntil',
        'costSourceSharedValidUntil',
        'costSourceType',
      ])
    : null;

/**
 * What a cost response does to the vendor quote Valid until the user gives, as the BE seeds the
 * line: only a line that comes to a vendor quote without one takes a value, a re-cost onto the same
 * vendor quote keeps what the user left, a cleared date included.
 */
export const getVendorQuoteValidUntilPrefill = ({
  initialCostData,
  nextCostData,
  previousCostData,
}: {
  initialCostData?: IQuotePartCostData | null;
  nextCostData?: IQuotePartCostData | null;
  previousCostData?: IQuotePartCostData | null;
}): VendorQuoteValidUntilPrefill => {
  const nextVendorQuoteId = getDatedVendorQuoteId(nextCostData);

  if (
    !nextVendorQuoteId ||
    nextCostData.costSourceQuoteValidUntil ||
    nextVendorQuoteId === getDatedVendorQuoteId(previousCostData)
  ) {
    return VendorQuoteValidUntilPrefill.KEEP;
  }

  return nextVendorQuoteId === getVendorQuoteId(initialCostData)
    ? VendorQuoteValidUntilPrefill.SAVED
    : VendorQuoteValidUntilPrefill.SHARED;
};

/**
 * Whether an empty vendor quote Valid until is posted as '', which the BE takes as a clear: only
 * when there is one to clear, the saved line's on the same vendor quote or the shared one the field
 * was prefilled with. Otherwise the key is left out and the BE seeds the line itself.
 */
export const hasVendorQuoteValidUntilToClear = (
  vendorQuoteCost?: TVendorQuoteCostFormValue | null,
  savedLine?: TOneTimeSpaRow | null
): boolean => {
  if (vendorQuoteCost?.costSourceSharedValidUntil) {
    return true;
  }

  return (
    isVendorQuoteWithoutValidUntil(savedLine) &&
    !!savedLine.costSourceValidUntil &&
    (!vendorQuoteCost || getVendorQuoteId(vendorQuoteCost) === getVendorQuoteId(savedLine))
  );
};

/**
 * Which validity dates the line form takes from the user, from form values and the saved row so
 * the fields, the schema and the payload agree: both on every typed cost; only Valid until on a
 * vendor quote cost whose vendor quote has none. The live cost's vendor quote answers first; else
 * the saved row's, while the live cost is still its vendor quote, as the form's vendor quote
 * number follows the cost while Overwrite is off. Never with a pick.
 */
export const getCostValidityEntry = ({
  hasSelectedSpa,
  isAlternateVersionEnabled,
  isCostingCustom,
  savedLine,
  vendorQuoteCost,
  vendorQuoteNumber,
}: {
  hasSelectedSpa?: boolean;
  isAlternateVersionEnabled: boolean;
  isCostingCustom?: boolean;
  savedLine?: TOneTimeSpaRow | null;
  vendorQuoteCost?: TVendorQuoteCostFormValue | null;
  vendorQuoteNumber?: string | null;
}): CostValidityEntry => {
  if (!isAlternateVersionEnabled || hasSelectedSpa) {
    return CostValidityEntry.NONE;
  }

  if (isCostingCustom) {
    return CostValidityEntry.TYPED;
  }

  if (vendorQuoteCost) {
    return isVendorQuoteWithoutValidUntil(vendorQuoteCost)
      ? CostValidityEntry.VENDOR_QUOTE_VALID_UNTIL
      : CostValidityEntry.NONE;
  }

  return isVendorQuoteWithoutValidUntil(savedLine) &&
    !!vendorQuoteNumber &&
    isSameVendorQuote(savedLine, vendorQuoteNumber)
    ? CostValidityEntry.VENDOR_QUOTE_VALID_UNTIL
    : CostValidityEntry.NONE;
};

// The same answer from a form's values, for the schema and the payload.
export const getFormCostValidityEntry = (
  {
    costSource,
    isCostingCustom,
    selectedSpa,
    vendorQuoteCost,
  }: {
    costSource?: ICostSourceFormValue | null;
    isCostingCustom?: boolean | null;
    selectedSpa?: ISelectedSpaFormValue | null;
    vendorQuoteCost?: TVendorQuoteCostFormValue | null;
  },
  isAlternateVersionEnabled: boolean,
  savedLine?: TOneTimeSpaRow | null
): CostValidityEntry =>
  getCostValidityEntry({
    hasSelectedSpa: !!selectedSpa?.spaCostSource,
    isAlternateVersionEnabled,
    isCostingCustom: !!isCostingCustom,
    savedLine,
    vendorQuoteCost,
    vendorQuoteNumber: costSource?.vendorQuoteNumber,
  });

// The vendor quote whose Valid since bounds the Valid until the user gives it.
export const getVendorQuoteValidSince = (
  vendorQuoteCost?: TVendorQuoteCostFormValue | null,
  savedLine?: TOneTimeSpaRow | null
): string | null => (vendorQuoteCost ?? savedLine)?.costSourceQuoteStartDate ?? null;

// The vendor quote's own dates; while it has no Valid until, the one the user gave the line.
const getVendorQuoteRowDates = (
  row: TVendorQuoteRow
): Pick<IOneTimeSpaRowDates, 'validSince' | 'validUntil'> => ({
  validSince: toDay(row.costSourceQuoteStartDate),
  validUntil: toDay(row.costSourceQuoteValidUntil) || toDay(row.costSourceValidUntil),
});

/**
 * A saved line's one-time SPA dates from its row: the rule on its saved costs (as the BE's
 * LineCostTypeService), then a vendor quote cost's vendor quote validity, or any other cost's
 * validity on its cost source. A vendor quote without Valid until takes the one the user gave on
 * the line's cost source, until the vendor quote gets its own.
 */
export const getOneTimeSpaRowDates = (row?: TOneTimeSpaRow | null): IOneTimeSpaRowDates => {
  const isOneTimeSpa =
    !!row &&
    isOneTimeSpaLine({
      costSourceType: row.costSourceType,
      externalReplacementCost: row.externalReplacementCost,
      isShipAndDebitVendor: row.supplier?.isShipAndDebit,
      purchaseCost: row.purchaseCost,
      replacementCost: row.replacementCost,
    });

  if (!isOneTimeSpa) {
    return { isOneTimeSpa: false, validSince: null, validUntil: null };
  }

  if (isVendorQuoteCost(row)) {
    return { isOneTimeSpa, ...getVendorQuoteRowDates(row) };
  }

  return {
    isOneTimeSpa,
    validSince: toDay(row.costSourceValidSince),
    validUntil: toDay(row.costSourceValidUntil),
  };
};

/**
 * The dates the cost info tooltip lists: a typed cost's and a vendor quote cost's whenever there is
 * one, any other cost's only on a one-time SPA line. None at all lists none.
 */
export const getCostInfoValidityDates = (
  row?: TOneTimeSpaRow | null
): Pick<IOneTimeSpaRowDates, 'validSince' | 'validUntil'> | null => {
  if (!row) {
    return null;
  }

  if (PricingSourceType.CUSTOM === row.costSourceType || isVendorQuoteCost(row)) {
    const dates =
      PricingSourceType.CUSTOM === row.costSourceType
        ? {
            validSince: toDay(row.costSourceValidSince),
            validUntil: toDay(row.costSourceValidUntil),
          }
        : getVendorQuoteRowDates(row);

    return dates.validSince || dates.validUntil ? dates : null;
  }

  const { isOneTimeSpa, validSince, validUntil } = getOneTimeSpaRowDates(row);

  return isOneTimeSpa ? { validSince, validUntil } : null;
};

/**
 * The single answer the cost section reads. A typed cost's and a vendor quote cost's dates show on
 * every line: the typed ones and a Valid until missing from the vendor quote are the user's, Valid
 * until required only for a one-time SPA; the vendor quote's own are read-only, from the live cost
 * as soon as it comes in, else from the saved row while the cost is still on its vendor quote. Any
 * other cost's show only on a one-time SPA line: the BE's read while the saved line is untouched,
 * otherwise known only once saved.
 */
export const resolveOneTimeSpaState = ({
  facts,
  hasCostChanged,
  hasSelectedSpa,
  isAlternateVersionEnabled,
  isCostingCustom,
  savedLine,
  vendorQuoteCost,
  vendorQuoteNumber,
}: {
  // The live cost's facts; for the dates the user gives only the form values count.
  facts: IOneTimeSpaFacts;
  hasCostChanged: boolean;
  hasSelectedSpa?: boolean;
  isAlternateVersionEnabled: boolean;
  isCostingCustom?: boolean;
  savedLine?: TOneTimeSpaRow | null;
  vendorQuoteCost?: TVendorQuoteCostFormValue | null;
  vendorQuoteNumber?: string | null;
}): IOneTimeSpaState => {
  if (!isAlternateVersionEnabled) {
    return NOT_ONE_TIME_SPA_STATE;
  }

  const entry = getCostValidityEntry({
    hasSelectedSpa,
    isAlternateVersionEnabled,
    isCostingCustom,
    savedLine,
    vendorQuoteCost,
    vendorQuoteNumber,
  });

  if (CostValidityEntry.NONE !== entry) {
    const isOneTimeSpa = isOneTimeSpaFormCost(facts);

    return {
      ...NOT_ONE_TIME_SPA_STATE,
      entry,
      isExpirationDateRequired: isOneTimeSpa,
      isOneTimeSpa,
      isShown: true,
      // A vendor quote cost's Valid since is always its vendor quote's.
      resolvedEffectiveDate:
        CostValidityEntry.VENDOR_QUOTE_VALID_UNTIL === entry
          ? toDay(getVendorQuoteValidSince(vendorQuoteCost, savedLine))
          : null,
    };
  }

  const isSavedVendorQuoteLive =
    !!savedLine &&
    isVendorQuoteCost(savedLine) &&
    (!hasCostChanged || (!!vendorQuoteNumber && isSameVendorQuote(savedLine, vendorQuoteNumber)));
  const readOnlyVendorQuote: TVendorQuoteRow | null =
    vendorQuoteCost ?? (isSavedVendorQuoteLive ? savedLine : null);

  if (readOnlyVendorQuote && !hasSelectedSpa) {
    const { validSince, validUntil } = getVendorQuoteRowDates(readOnlyVendorQuote);

    return {
      ...NOT_ONE_TIME_SPA_STATE,
      isOneTimeSpa:
        hasCostChanged || !savedLine
          ? isOneTimeSpaLine(facts)
          : getOneTimeSpaRowDates(savedLine).isOneTimeSpa,
      isShown: true,
      resolvedEffectiveDate: validSince,
      resolvedExpirationDate: validUntil,
    };
  }

  // A saved typed cost reaching here had Overwrite switched off, so its row no longer holds.
  if (savedLine && !hasCostChanged && PricingSourceType.CUSTOM !== savedLine.costSourceType) {
    const { isOneTimeSpa, validSince, validUntil } = getOneTimeSpaRowDates(savedLine);

    return isOneTimeSpa
      ? {
          ...NOT_ONE_TIME_SPA_STATE,
          isOneTimeSpa,
          isShown: true,
          resolvedEffectiveDate: validSince,
          resolvedExpirationDate: validUntil,
        }
      : NOT_ONE_TIME_SPA_STATE;
  }

  return isOneTimeSpaLine(facts)
    ? { ...NOT_ONE_TIME_SPA_STATE, isDatesPendingSave: true, isOneTimeSpa: true, isShown: true }
    : NOT_ONE_TIME_SPA_STATE;
};

export const isOneTimeSpaExpirationDateMissing = (row?: TOneTimeSpaRow | null): boolean => {
  const { isOneTimeSpa, validUntil } = getOneTimeSpaRowDates(row);

  return isOneTimeSpa && !validUntil;
};

// The BE judges by the server's day, so the stored day is compared with the New York day.
export const isOneTimeSpaExpirationDatePast = (
  row?: TOneTimeSpaRow | null,
  now: Dayjs = dayjs()
): boolean => {
  const { isOneTimeSpa, validUntil } = getOneTimeSpaRowDates(row);

  return (
    isOneTimeSpa && !!validUntil && validUntil < now.tz(SERVER_TIMEZONE).format(DB_DATE_FORMAT)
  );
};
